import "./FlaggingModal.css";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import AutoSuggest from "react-autosuggest";
import ReactModal from "react-modal";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { LabStainList, SlideFlagPOST, Stain, User } from "pwapi";

import { StainList } from "custom-models/frontLoader";

import { caseSearchToURL } from "shared/actions/casesActions";
import { createFlag } from "shared/actions/flagActions";
import { addToast, nextId } from "shared/actions/toastActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import selectLabStains from "shared/selectors/selectLabStains";
import { selectSortedCaseDetailSlides } from "shared/selectors/selectSortedCaseDetail";
import { getNextCaseId } from "shared/utils/caseUtils";
import { splitFlagReason } from "shared/utils/labOrderUtils";
import { getWrapAroundSlide, slideDetailLabel } from "shared/utils/slideUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import SelectInput from "components/customInputs/SelectInput";
import TextareaInput from "components/customInputs/TextareaInput";
import ApplicationState from "store/ApplicationState";
import { useAppDispatch, useAppSelector } from "store/hooks";

import LabStainInput from "./components/LabStainInput";
import {
  getReasonCatalog,
  initializeReasons,
} from "./FlaggingModal.configurations";
import { RequestLabels } from "./FlaggingModal.enum";

type FlaggingModalProps = {
  caseDetail: CaseDetailState;
  slideDetail: SlideDetailState;
  isOpen: boolean;
  setIsOpen: Function;
};
type FlagForm = {
  request: SlideFlagPOST.order_request;
  note?: string;
  reason: string;
  stain?: string;
  lab_stain?: LabStainList | null;
};

const FlaggingModal = ({
  caseDetail,
  slideDetail,
  isOpen,
  setIsOpen,
}: FlaggingModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // State for old stain based flag request selection.
  const [suggestions, setSuggestions] = useState<
    ReturnType<typeof selectLabStains>
  >([]);
  const stains = useAppSelector(({ stains }) => stains);
  const labStains = useAppSelector((state) =>
    selectLabStains(state, caseDetail.accessioning_lab?.id || 0, true)
  );
  const slideStain = stains[slideDetail.stain_id || 0];

  const newSlideFlagOrdering = useAppSelector((state) =>
    selectFeatureFlag(state, "newSlideFlagOrdering")
  );

  const user: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );

  const {
    currentCaseSearch: searchParams,
    caseIdList,
    caseIdListPending,
  } = useSelector(({ cases }: ApplicationState) => cases);

  const sortedSlidesList = useAppSelector(selectSortedCaseDetailSlides);

  const findStain = (stain: string = "") =>
    Object.values(stains).find(
      (s) => s.abbreviation.toUpperCase() === stain.toUpperCase()
    );

  const getNextCaseUrl = (): string => {
    const nextCaseId = getNextCaseId(
      caseDetail.id,
      caseIdListPending,
      caseIdList
    );

    return nextCaseId
      ? `/cases/${nextCaseId}`
      : caseSearchToURL(searchParams, user);
  };

  const getNextSlideUrl = (): string => {
    const nextSlide = getWrapAroundSlide(sortedSlidesList, slideDetail);
    return nextSlide
      ? `/cases/${caseDetail.id}/slides/${nextSlide.id}`
      : getNextCaseUrl();
  };

  const getStain = (
    request?: SlideFlagPOST.order_request,
    stain?: string
  ): StainList | undefined => {
    // If stain Provided, Find and return it.
    if (!!stain) return findStain(stain);

    // No Request and No Stain.  ABORT.
    if (!request) return;

    // If Request has been mapped, retrieve abbr and find stain. Else, return the stain attached to slide.
    const mapRequestToStainAbbr: Partial<
      Record<SlideFlagPOST.order_request, string>
    > & { recut: string } = {
      reembed: "REMBD",
      recut: "RCT1",
    };
    return request in mapRequestToStainAbbr
      ? findStain(mapRequestToStainAbbr[request])
      : slideStain;
  };

  const handleOnSubmit = (values: FlagForm) => {
    const stain = getStain(values.request, values.stain);
    console.log(values);

    const form = {
      slide_id: slideDetail.id || 0,
      reason:
        SlideFlagPOST.reason[
          values.reason
            .toUpperCase()
            .replace(/-/g, "_") as keyof typeof SlideFlagPOST.reason
        ],
      note: values.note,
      ...(!!newSlideFlagOrdering
        ? {
            ...(!!values.request &&
              !values.lab_stain?.id && {
                order_request:
                  (values.request as string) === "recut"
                    ? SlideFlagPOST.order_request.REPEAT
                    : values.request,
              }),
            lab_stain_id: values.lab_stain?.id || undefined,
          }
        : {
            is_rescan: values.request === SlideFlagPOST.order_request.RESCAN,
            stain_id: stain?.id,
          }),
    };

    dispatch(
      createFlag({
        parentLookupSlideId: form.slide_id,
        requestBody: form,
        isMislabel: values.reason === "mislabeled",
      })
    )
      .then(() => {
        setIsOpen(false);
        if (values.reason === "mislabeled") {
          const isSlide = caseDetail.slides.length > 1;
          const url = isSlide ? getNextSlideUrl() : getNextCaseUrl();

          const toastStr = isSlide ? "Slide" : "Case";

          dispatch(
            addToast(
              {
                kind: "success",
                title: `${toastStr} has been moved`,
                message:
                  `${toastStr} has been moved back to the QC team to review the mislabeled slide.\\n` +
                  `For patient safety the ${toastStr.toLowerCase()} is no longer visible until that has been resolved.`,
                removalTimeout: 4000,
              },
              nextId()
            )
          );
          navigate(url, { replace: true });
        }
      })
      .catch(() => {
        setIsOpen(false);
      });
  };

  const onFilterStains = (value: string) =>
    labStains.filter(
      (s) =>
        s.stain?.category === Stain.category.TECH &&
        (s.stain?.name.toUpperCase().startsWith(value.toUpperCase()) ||
          s.stain?.abbreviation.startsWith(value.toUpperCase()))
    );

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      className="Modal"
      overlayClassName="Overlay"
    >
      <Formik
        initialValues={{
          reason: "",
          request: "" as SlideFlagPOST.order_request,
          note: undefined,
          stain: "",
          lab_stain: null,
        }}
        enableReinitialize
        onSubmit={handleOnSubmit}
      >
        {({ values }) => {
          const reasonCatalog = getReasonCatalog(slideStain as Stain);
          const reasonAttributes =
            reasonCatalog[values.reason.toUpperCase().replace(/-/g, "_")];
          const requestList = (reasonAttributes?.requestList || []).filter(
            (r) =>
              newSlideFlagOrdering
                ? r
                : r !== SlideFlagPOST.order_request.DEEPEP
          );
          return (
            <Form className="FlaggingModal">
              <span className="modal__header" data-testid="header">
                Flagging{" "}
                <strong>
                  {slideDetailLabel(slideDetail, slideStain, true)}
                </strong>
              </span>
              <div className="modal__select">
                <Field name="reason">
                  {({ field, form }: any) => (
                    <SelectInput
                      label="Reason"
                      id="reason"
                      required
                      options={Object.values(SlideFlagPOST.reason)
                        .filter((r) => r !== "incomplete-scan")
                        .map((p) => ({
                          label: splitFlagReason(p),
                          value: p,
                        }))}
                      {...field}
                      onChange={(e) => {
                        form.setValues({
                          reason: e.target.value,
                          request: initializeReasons.includes(
                            e.target.value as SlideFlagPOST.reason
                          )
                            ? reasonCatalog[
                                e.target.value.toUpperCase().replace(/-/g, "_")
                              ].requestList?.[0] || ""
                            : "",
                          stain: newSlideFlagOrdering
                            ? ""
                            : reasonCatalog[
                                e.target.value.toUpperCase().replace(/-/g, "_")
                              ].initialStain || "",
                          lab_stain: null,
                          note: form.values.note,
                        });
                      }}
                    />
                  )}
                </Field>
              </div>
              {values.reason && (
                <div className="modal__multifield">
                  {!!requestList.length && (
                    <Field name="request">
                      {({ field, form }: any) => {
                        return (
                          <div className="checkbox__container">
                            {requestList.map((val: string) => {
                              const stainCheck = newSlideFlagOrdering
                                ? !!form.values.lab_stain
                                : !!form.values.stain;
                              return (
                                <label key={val}>
                                  <input
                                    type="checkbox"
                                    checked={!stainCheck && field.value === val}
                                    onChange={() => {
                                      console.log(
                                        field.value,
                                        val,
                                        field.value === val
                                          ? undefined
                                          : val === "recut"
                                          ? SlideFlagPOST.order_request.REPEAT
                                          : val
                                      );
                                      form.setValues({
                                        ...form.values,
                                        request: field.value === val ? "" : val,
                                        order_request:
                                          field.value === val ? undefined : val,
                                        stain: "",
                                        lab_stain: null,
                                      });
                                    }}
                                  />
                                  {
                                    RequestLabels[
                                      val as keyof typeof RequestLabels
                                    ]
                                  }
                                </label>
                              );
                            })}
                          </div>
                        );
                      }}
                    </Field>
                  )}
                  {reasonAttributes?.canChooseStain && (
                    <>
                      {!!requestList.length && (
                        <span className="modal__multifield--spacer">
                          - OR -
                        </span>
                      )}
                      {newSlideFlagOrdering ? (
                        <Field name="lab_stain">
                          {({ field, form }: any) => (
                            <LabStainInput
                              value={field.value}
                              serviceOptions={{
                                stainCategory: Stain.category.TECH,
                                //@ts-ignore TODO: Fix this openapi type issue.
                                isArchived: false,
                                parentLookupLabId:
                                  caseDetail.accessioning_lab?.id,
                              }}
                              onChange={(value?: LabStainList) => {
                                value &&
                                  form.setValues({
                                    ...form.values,
                                    lab_stain: value,
                                    request: undefined,
                                  });
                              }}
                            />
                          )}
                        </Field>
                      ) : (
                        <Field name="stain">
                          {({ field, form }: any) => (
                            <CustomInput id="TECH" label="Lab Order (Optional)">
                              <AutoSuggest
                                suggestions={suggestions}
                                getSuggestionValue={(suggestion) =>
                                  suggestion.stain?.abbreviation || ""
                                }
                                inputProps={{
                                  id: "TECH",
                                  name: "TECH",
                                  alt: "newStain__input",
                                  value: field.value,
                                  onChange: (_, { newValue }) => {
                                    form.setValues({
                                      ...form.values,
                                      stain: newValue,
                                      request: "",
                                    });
                                  },
                                }}
                                onSuggestionsFetchRequested={({ value }) => {
                                  const filtered = onFilterStains(value);
                                  setSuggestions(filtered);
                                }}
                                onSuggestionsClearRequested={() => {
                                  setSuggestions([]);
                                }}
                                renderSuggestion={(s) => (
                                  <div>
                                    <strong>{s.stain?.abbreviation}</strong> (
                                    {s.stain?.name})
                                  </div>
                                )}
                              />
                            </CustomInput>
                          )}
                        </Field>
                      )}
                    </>
                  )}
                </div>
              )}
              <Field name="note">
                {({ field, form }: any) => (
                  <TextareaInput
                    label="Additional Details"
                    id="additional_details"
                    required={form.values.reason === "other"}
                    {...field}
                  />
                )}
              </Field>
              <div className="button__container">
                <PathologyWatchButton
                  type="submit"
                  apiService={`/api/slides/${slideDetail.id}/flags/`}
                >
                  Flag
                </PathologyWatchButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </ReactModal>
  );
};

export default FlaggingModal;
